import { useMemo } from "react";
import { UseFormWatch } from "react-hook-form";
import { PayeeFormInputs } from "./types";

interface PaymentDocumentData {
  document?: {
    id: string;
    title: string;
    documentType: any;
    status: "saved" | "pending";
  } | null;
  paymentInvoices?: Array<{
    id: string;
    invoiceDocument?: {
      id: string;
      title: string;
      documentType: any;
    } | null;
    status: "saved" | "pending";
  }> | null;
}

interface UsePaymentDocumentDataProps {
  paymentId?: string;
  existingPayment?: any;
  formFields: any[];
  watch: UseFormWatch<PayeeFormInputs>;
}

/**
 * Custom hook that normalizes payment document data from form state and existing payment
 * into a consistent format for PaymentDocumentsTable.
 *
 * This eliminates the complex conditional logic in InvoicesAttachmentsSection by
 * centralizing the data transformation logic.
 */
export const usePaymentDocumentData = ({
  paymentId,
  existingPayment,
  formFields,
  watch,
}: UsePaymentDocumentDataProps): PaymentDocumentData => {
  const currentReceiptDocument = watch("receiptDocument");

  return useMemo(() => {
    // For new payments: All documents are pending (from form state)
    if (!paymentId) {
      return {
        document: currentReceiptDocument
          ? {
              ...currentReceiptDocument,
              status: "pending" as const,
            }
          : null,
        paymentInvoices: formFields.map((invoice) => ({
          id: invoice.id || "",
          invoiceDocument: invoice.invoiceDocument
            ? {
                id: invoice.invoiceDocument.id || "",
                title: invoice.invoiceDocument.title || "",
                documentType: invoice.invoiceDocument.documentType,
              }
            : null,
          status: "pending" as const,
        })),
      };
    }

    // For existing payments: Mix of saved and pending documents
    if (existingPayment) {
      const originalReceipt = existingPayment.document;

      return {
        document: (() => {
          if (currentReceiptDocument) {
            return {
              ...currentReceiptDocument,
              status:
                currentReceiptDocument.id === originalReceipt?.id
                  ? "saved"
                  : "pending",
            };
          }
          return originalReceipt
            ? { ...originalReceipt, status: "saved" as const }
            : null;
        })(),
        paymentInvoices:
          existingPayment.paymentInvoices?.map((invoice: any) => ({
            ...invoice,
            status: "saved" as const,
          })) || [],
      };
    }

    // Fallback: empty state
    return {
      document: null,
      paymentInvoices: [],
    };
  }, [paymentId, existingPayment, formFields, currentReceiptDocument]);
};
